import { DateTime } from "luxon"

export interface OpeningHour {
  id: string
  kind?: string | null
  weekday?: number | null
  startDate?: string | null
  endDate?: string | null
  startTime?: string | null
  endTime?: string | null
  closed?: boolean | null
}

export const isVenueOpen = (openingHours: OpeningHour[], timezone: string): boolean => {
  const now = DateTime.now().setZone(timezone)
  const currentWeekday = now.weekday % 7

  const todaySpecial = openingHours.find(
    (h) =>
      h.kind === "special" &&
      h.startDate &&
      h.endDate &&
      now >= DateTime.fromISO(h.startDate).setZone(timezone) &&
      now <= DateTime.fromISO(h.endDate).setZone(timezone),
  )

  if (todaySpecial) {
    if (todaySpecial.closed) return false
    if (!todaySpecial.startTime || !todaySpecial.endTime) return false

    const [startHour, startMinute] = todaySpecial.startTime.split(":").map(Number)
    const [endHour, endMinute] = todaySpecial.endTime.split(":").map(Number)
    const currentMinutes = now.hour * 60 + now.minute
    const startMinutes = startHour * 60 + startMinute
    const endMinutes = endHour * 60 + endMinute

    return currentMinutes >= startMinutes && currentMinutes < endMinutes
  }

  const todayRegular = openingHours.find((h) => h.kind === "regular" && h.weekday === currentWeekday)

  if (!todayRegular) return false
  if (todayRegular.closed) return false
  if (!todayRegular.startTime || !todayRegular.endTime) return false

  const [startHour, startMinute] = todayRegular.startTime.split(":").map(Number)
  const [endHour, endMinute] = todayRegular.endTime.split(":").map(Number)
  const currentMinutes = now.hour * 60 + now.minute
  const startMinutes = startHour * 60 + startMinute
  const endMinutes = endHour * 60 + endMinute

  return currentMinutes >= startMinutes && currentMinutes < endMinutes
}

export const getOpeningTime = (openingHours: OpeningHour[], timezone: string): string | null => {
  const now = DateTime.now().setZone(timezone)
  const currentWeekday = now.weekday % 7

  const todaySpecial = openingHours.find(
    (h) =>
      h.kind === "special" &&
      h.startDate &&
      h.endDate &&
      now >= DateTime.fromISO(h.startDate).setZone(timezone) &&
      now <= DateTime.fromISO(h.endDate).setZone(timezone),
  )

  if (todaySpecial) {
    return todaySpecial.closed ? null : todaySpecial.startTime
  }

  const todayRegular = openingHours.find((h) => h.kind === "regular" && h.weekday === currentWeekday)

  return todayRegular?.closed ? null : todayRegular?.startTime || null
}

export const getTodayHours = (
  openingHours: OpeningHour[],
  timezone: string,
): { startTime: string; endTime: string } | null => {
  const now = DateTime.now().setZone(timezone)
  const currentWeekday = now.weekday % 7

  const todaySpecial = openingHours.find(
    (h) =>
      h.kind === "special" &&
      h.startDate &&
      h.endDate &&
      now >= DateTime.fromISO(h.startDate).setZone(timezone) &&
      now <= DateTime.fromISO(h.endDate).setZone(timezone),
  )

  if (todaySpecial) {
    if (todaySpecial.closed || !todaySpecial.startTime || !todaySpecial.endTime) return null
    return { startTime: todaySpecial.startTime, endTime: todaySpecial.endTime }
  }

  const todayRegular = openingHours.find((h) => h.kind === "regular" && h.weekday === currentWeekday)

  if (!todayRegular || todayRegular.closed || !todayRegular.startTime || !todayRegular.endTime) return null

  return { startTime: todayRegular.startTime, endTime: todayRegular.endTime }
}

export const getWeekdayHours = (openingHours: OpeningHour[], weekday: number, date: DateTime): OpeningHour | null => {
  const special = openingHours.find(
    (h) =>
      h.kind === "special" &&
      h.startDate &&
      h.endDate &&
      date >= DateTime.fromISO(h.startDate) &&
      date <= DateTime.fromISO(h.endDate),
  )

  if (special) return special

  return openingHours.find((h) => h.kind === "regular" && h.weekday === weekday) || null
}

export const getOpeningHoursForDate = (
  openingHours: OpeningHour[] | undefined,
  date: DateTime | null,
): { startHour: number; endHour: number; closed: boolean } => {
  if (!openingHours || !date) {
    return { startHour: 8, endHour: 23, closed: false }
  }

  const weekday = date.weekday % 7
  const hours = getWeekdayHours(openingHours, weekday, date)

  if (!hours || hours.closed || !hours.startTime || !hours.endTime) {
    return { startHour: 8, endHour: 23, closed: true }
  }

  const [startHour] = hours.startTime.split(":").map(Number)
  const [endHour] = hours.endTime.split(":").map(Number)

  return { startHour, endHour, closed: false }
}

export interface EffectiveCalendarRange {
  effectiveStartHour: number
  effectiveEndHour: number
  openingStartHour: number
  openingEndHour: number
  closed: boolean
}

/**
 * Calculate the effective calendar range based on reservations and opening hours.
 * If reservations exist outside opening hours, the calendar range is extended to include them.
 * This ensures reservations are always visible even if they fall outside the venue's opening hours.
 *
 * @param openingHours - The opening hours for the date (startHour, endHour, closed)
 * @param reservations - Array of reservations with startTime and endTime as DateTime objects
 * @returns The effective calendar range and original opening hours
 */
export const getEffectiveCalendarRange = (
  openingHours: { startHour: number; endHour: number; closed: boolean },
  reservations: Array<{ startTime: DateTime; endTime: DateTime }> | undefined | null,
): EffectiveCalendarRange => {
  const { startHour, endHour, closed } = openingHours

  // If no reservations, use opening hours as-is
  if (!reservations || reservations.length === 0) {
    return {
      effectiveStartHour: startHour,
      effectiveEndHour: endHour,
      openingStartHour: startHour,
      openingEndHour: endHour,
      closed,
    }
  }

  // Handle cross-midnight opening hours
  // If endHour < startHour, treat endHour as next day (add 24)
  const isCrossMidnight = endHour < startHour
  const openingEndWrapped = isCrossMidnight ? endHour + 24 : endHour

  let minHour = startHour
  let maxHourWrapped = openingEndWrapped

  for (const reservation of reservations) {
    const resStartHour = reservation.startTime.hour
    const resEndHour = reservation.endTime.hour
    const resEndMinute = reservation.endTime.minute

    // Round down start time to the nearest hour
    const resStartRounded = resStartHour

    // Round up end time to the nearest hour (if there are minutes, go to next hour)
    const resEndRounded = resEndMinute > 0 ? resEndHour + 1 : resEndHour

    // For early reservations (before opening time)
    if (resStartRounded < startHour) {
      minHour = Math.min(minHour, resStartRounded)
    }

    // For late reservations, we need to determine if they extend past closing
    // Wrap the reservation end hour for comparison in cross-midnight scenarios
    let resEndWrapped = resEndRounded

    if (isCrossMidnight) {
      // In cross-midnight mode, hours 0-endHour are considered as 24-24+endHour
      if (resEndRounded <= endHour) {
        resEndWrapped = resEndRounded + 24
      } else if (resEndRounded < startHour) {
        // Reservation ends in the "gap" before opening starts again
        resEndWrapped = resEndRounded + 24
      }
    } else {
      // In non-cross-midnight mode, if reservation ends at or before start hour
      // (e.g., ends at 0 = midnight when venue is 10-20), treat as crossing midnight
      if (resEndRounded <= startHour && resStartRounded >= startHour) {
        resEndWrapped = resEndRounded + 24
      }
    }

    // Check if the reservation extends past the closing time
    if (resEndWrapped > maxHourWrapped) {
      maxHourWrapped = resEndWrapped
    }

    // Also check start time - if it's after closing (in non-cross-midnight mode)
    // or in the gap (in cross-midnight mode), we need to extend
    if (!isCrossMidnight && resStartRounded >= endHour) {
      // Non-cross-midnight: reservation starts after venue closes
      minHour = Math.min(minHour, resStartRounded)
    }
  }

  // Convert back from wrapped hours to actual hours
  const effectiveEndHour = maxHourWrapped >= 24 ? maxHourWrapped - 24 : maxHourWrapped

  return {
    effectiveStartHour: minHour,
    effectiveEndHour,
    openingStartHour: startHour,
    openingEndHour: endHour,
    closed,
  }
}
